/**

=========================================================

* Trust Cargo - Customer Payments (money in)

=========================================================



* A faithful rebuild of the PAYMENT sheet: same columns, same order, same

* banded look - but the numbers are queried, not typed.

*

* Deposit and payment stay as two separate columns exactly as the office

* writes them. `balance` is the order's outstanding amount, which is why the

* same figure repeats on every row of the same CODE - the sheet's SUMIF did

* the same thing.

*/

import React, { useState, useEffect } from "react";

import Card from "@mui/material/Card";

import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import SuiBox from "components/SuiBox";

import SuiTypography from "components/SuiTypography";

import CargoLoader from "components/CargoLoader";

import FilterBar, { filterRows } from "components/FilterBar";

import Pagination, { usePagination } from "components/Pagination";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import Footer from "examples/Footer";

const API = "https://trust-cargo.co/anas_backend/api/v2";

const COLUMNS = [
  // the row's own auto-increment id, ahead of the sheet's CODE
  { key: "id", label: "ID", width: 70 },
  { key: "code", label: "CODE", width: 90 },

  { key: "shop_code", label: "shop code", width: 130 },

  { key: "con_no", label: "Con No.", width: 110 },

  { key: "pay_date", label: "pay date", width: 130 },

  { key: "deposit", label: "Deposit", width: 120, money: true },

  { key: "payment", label: "payment", width: 120, money: true },

  { key: "balance", label: "balance", width: 140, money: true },
];

const money = (v) =>
  v === null || v === undefined || v === "" ? "" : Number(v).toLocaleString("en-US");

// dd/mm/yyyy, the way the sheet writes it

const asSheetDate = (iso) => {
  if (!iso) return "";

  const [y, m, d] = String(iso).split("-");

  return `${d}/${m}/${y}`;
};

function CustomerPayments() {
  const [rows, setRows] = useState([]);

  const [query, setQuery] = useState("");

  const [from, setFrom] = useState("");

  const [to, setTo] = useState("");

  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);

    try {
      const qs = new URLSearchParams();

      if (from) qs.set("from", from);

      if (to) qs.set("to", to);

      const res = await fetch(`${API}/payments?${qs.toString()}`);

      const json = await res.json();

      // totals come back too, but the header shows the totals of what is

      // actually on screen after filtering, so they are summed below instead

      setRows(json.rows || []);
    } catch (e) {
      setRows([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [from, to]);

  const filteredRows = filterRows(rows, query);

  const pager = usePagination(filteredRows);

  // running totals of what is on screen, so filtering a container or a month

  // answers "how much came in" without exporting anything

  const shownDeposit = filteredRows.reduce((s, r) => s + (Number(r.deposit) || 0), 0);

  const shownPayment = filteredRows.reduce((s, r) => s + (Number(r.payment) || 0), 0);

  const headCell = {
    fontWeight: 800,

    fontSize: 12.5,

    textAlign: "center",

    textTransform: "none",

    letterSpacing: 0,

    whiteSpace: "nowrap",
  };

  return (
    <DashboardLayout>
      <SuiBox py={1}>
        <Card>
          {/* title, dates, search, totals and count - one row. The date inputs

              carry their own placeholder text so they need no labels above. */}

          <FilterBar
            title="Customer Payments"
            actions={
              <SuiTypography variant="caption" sx={{ opacity: 0.75, whiteSpace: "nowrap" }}>
                {`Deposit ${money(shownDeposit)} · payment ${money(shownPayment)}`}
              </SuiTypography>
            }
            value={query}
            onChange={setQuery}
            count={filteredRows.length}
            total={rows.length}
            placeholder="Search by code, shop code or container..."
          >
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              title="From date"
              style={{
                padding: "8px 10px",

                borderRadius: 8,

                border: "1px solid rgba(11,27,58,0.15)",

                fontFamily: "inherit",

                fontSize: 13,
              }}
            />

            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              title="To date"
              style={{
                padding: "8px 10px",

                borderRadius: 8,

                border: "1px solid rgba(11,27,58,0.15)",

                fontFamily: "inherit",

                fontSize: 13,
              }}
            />

            {(from || to) && (
              <SuiTypography
                variant="caption"
                fontWeight="bold"
                sx={({ palette }) => ({ cursor: "pointer", color: palette.error.main })}
                onClick={() => {
                  setFrom("");

                  setTo("");
                }}
              >
                Clear dates
              </SuiTypography>
            )}
          </FilterBar>

          {loading ? (
            <CargoLoader label="Loading payments" />
          ) : (
            <SuiBox px={3} pb={3} sx={{ overflowX: "auto" }}>
              <>
                <Table
                  sx={({ palette, functions: { rgba } }) => ({
                    minWidth: 820,

                    borderCollapse: "collapse",

                    // the sheet's grid: every cell outlined, like Excel

                    "& td, & th": {
                      border: `1px solid ${rgba(palette.dark.main, 0.18)}`,
                    },
                  })}
                >
                  <TableHead>
                    <TableRow>
                      {COLUMNS.map((c) => (
                        <TableCell
                          key={c.key}
                          sx={({ palette, functions: { rgba } }) => ({
                            ...headCell,

                            width: c.width,

                            // the blue-grey header band from the sheet

                            backgroundColor: rgba(palette.info.main, 0.22),

                            color: palette.dark.main,
                          })}
                        >
                          {c.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {pager.pageRows.map((r) => (
                      <TableRow key={r.id}>
                        {COLUMNS.map((c) => {
                          let value;

                          if (c.key === "pay_date") value = asSheetDate(r.pay_date);
                          else if (c.money) value = money(r[c.key]);
                          else value = r[c.key] ?? "";

                          const negative = c.key === "balance" && Number(r.balance) < 0;

                          return (
                            <TableCell
                              key={c.key}
                              sx={({ palette }) => ({
                                textAlign: "center",

                                fontWeight: 700,

                                fontSize: 13,

                                py: 1,

                                // a negative balance means the customer overpaid

                                color: negative ? palette.error.main : palette.dark.main,
                              })}
                            >
                              {value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}

                    {filteredRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={COLUMNS.length} sx={{ textAlign: "center", py: 4 }}>
                          <SuiTypography variant="button" color="text">
                            No payments
                          </SuiTypography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                <Pagination {...pager} />
              </>
            </SuiBox>
          )}
        </Card>
      </SuiBox>

      <Footer />
    </DashboardLayout>
  );
}

export default CustomerPayments;
